/**
 * SG 頁的 API 層（薄，跟 `api.ts`／`http-client.ts` 同一套慣例：手寫 fetch、錯誤翻成人話）。
 *
 * 為什麼不掛進 `@chat-rea/console-ui` 的 `ApiClient`：那些 `sg` 端點不是收件匣契約的一部分，
 * 而且認證走的是另一條路（見下）。這一層獨立，才不會把 SG 的例外塞進全站介面。
 *
 * ── 金鑰（這是本頁最重要的一條）────────────────────────────────────────
 * `/api/sg/*` 只認**服務金鑰** （notes/service-auth-sg-agent-key.md：與
 * `env.SUGARGIRL_AGENT_KEY` 常數時間比對，不走 vm-auth、也不要 X-Api-Token）。
 *
 * 這把金鑰可以代聊**任何**女孩，等於整個服務的身分，**不能**寫進前端 bundle、`/config.json`
 * （公開靜態檔）或 localStorage。做法是**伺服器端注入**：
 *
 *   瀏覽器 →（同源、不帶任何金鑰）`/api/sg-proxy/…` → Pages Function
 *   （由伺服器端補上 Authorization 標頭）→ chat-rea-proxy `/api/sg/…`
 *
 * 金鑰只存在 Pages 的環境變數（`wrangler pages secret put SUGARGIRL_AGENT_KEY`），
 * 瀏覽器永遠看不到它；前端也**沒有**手動貼金鑰的後門（舊的 `window.__SG_AGENT_KEY__`
 * 機制已移除）。
 *
 * 代理是部署的一部分，正式站因此不需要任何額外設定。若是本機 `vite dev`（只有 Vite、
 * 沒有 Pages Functions），這些路徑會回 index.html，這時丟 `SgProxyUnavailableError`
 * 說清楚「代理不在」，而不是假裝後端壞掉、更不退回假資料。
 *
 * 「接上／退出／改模式／看草稿」走 `/api/sessions`（一般 session 驗證 + X-Api-Token），
 * 本來就不需要 服務金鑰；後端 `POST /api/sessions` 接受 `service:'sg'`。
 */
import { API_BASE, IS_MOCK } from '../api';
import { vmSessionHeaders } from '../auth/session';
import {
  parseMode,
  type AutoReplyMode,
  type AutoReplyStatus,
  type SgConversation,
  type SgDraft,
  type SgGirl,
  type SgSession,
} from './types';

/** 本網域的 SG 代理（Pages Functions，見 `functions/api/sg-proxy/[[path]].ts`）。 */
const SG_PROXY_ROOT = '/api/sg-proxy';

/** 代理不在（例如只用 `vite dev`、沒有 Pages Functions）——要跟「後端壞了」分得出來。 */
export class SgProxyUnavailableError extends Error {
  readonly code = 'sg_proxy_unavailable';
  constructor(detail = '') {
    super(
      `SG 代理不在（${SG_PROXY_ROOT}）：這一頁需要 Cloudflare Pages Functions。` +
        '本機請用 `npm run build` 後 `npx wrangler pages dev dist`，或直接開正式站。' +
        (detail === '' ? '' : `（${detail}）`),
    );
    this.name = 'SgProxyUnavailableError';
  }
}

/**
 * `X-Api-Token` 的存放位置。跟 `@chat-rea/console-ui` 的 `API_TOKEN_STORAGE_KEY` **同一個 key**：
 * 那支常數沒有從套件 index 匯出，而 console 這個 app 的型別是吃 `packages/console-ui/dist`
 * （build 產物），多匯出一個名字就得先重建 dist 才 typecheck 得過。與其為了讀一個字串改共用套件，
 * 這裡照既有 key 直接讀，並且只讀不寫（寫入仍由畫面上的 `ApiTokenBar` 負責）。
 */
const API_TOKEN_STORAGE_KEY = 'chat-rea.api.token';

function readApiToken(): string {
  try {
    return sessionStorage.getItem(API_TOKEN_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

/** 代理路徑（相對於本網域的 `/api/sg-proxy`）。 */
function proxyPath(path: string): string {
  return `${SG_PROXY_ROOT}${path.startsWith('/') ? path : `/${path}`}`;
}

function baseUrl(): string {
  if (IS_MOCK || API_BASE === '') {
    throw new Error('目前是 mock 模式（沒有後端位址），SG 頁需要真後端：在 /config.json 設 apiBase');
  }
  return API_BASE.replace(/\/+$/, '');
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  /**
   * 走同源的 SG 代理（`/api/sg-proxy/*`）。金鑰由 Pages Function 注入，
   * 本端不帶任何憑證、也不經 API_BASE。只有 `/api/sg/*` 的讀取會用到。
   */
  viaProxy?: boolean;
};

/**
 * 唯一出口。標頭沿用全站慣例：
 * - 認證：`vmSessionHeaders()`（PIN 或 `x-vm-session`），每次呼叫重讀。
 * - 寫入類：`x-api-token`（`SERVICE_TOKEN`）——2026-10-27 起 worker 不再要求它，
 *   session 就夠；這裡保留「有就帶」只是相容舊部署。
 * - `viaProxy`：改打本網域代理，且不加任何授權標頭——金鑰只在伺服器端。
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const writeToken = method === 'GET' ? '' : readApiToken();
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...vmSessionHeaders(),
    ...(writeToken === '' ? {} : { 'x-api-token': writeToken }),
  };
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  const url = options.viaProxy ? proxyPath(path) : `${baseUrl()}${path}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      cache: 'no-store',
      credentials: 'omit',
    });
  } catch (err) {
    throw new Error(`連不到後端（${method} ${url}）：${err instanceof Error ? err.message : String(err)}`);
  }

  // 代理不在時最常見的兩種假回應：SPA fallback 的 200 + index.html、或路由根本沒有的 404。
  if (options.viaProxy) {
    const contentType = res.headers.get('content-type') ?? '';
    if (res.status === 404) throw new SgProxyUnavailableError('404');
    if (!contentType.includes('json')) {
      throw new SgProxyUnavailableError(
        `${res.status} ${contentType === '' ? '無 content-type' : contentType}`,
      );
    }
  }

  const raw = await res.text();
  let body: unknown;
  try {
    body = raw === '' ? undefined : (JSON.parse(raw) as unknown);
  } catch {
    body = raw;
  }
  if (!res.ok) {
    const row = asRecord(body);
    const code = typeof row.code === 'string' ? row.code : '';
    const detail = typeof row.error === 'string' ? row.error : `${res.status} ${res.statusText}`;
    const message =
      code === 'sg_proxy_not_configured'
        ? 'Pages 沒有設定 SUGARGIRL_AGENT_KEY，SG 代理目前是關閉的（要管理員設 secret）'
        : code === 'sg_not_configured'
          ? '後端沒有設定 SG（SUGARGIRL_BASE_URL／SUGARGIRL_AGENT_KEY），SG 端點目前是關閉的'
          : code === 'sg_key_missing'
            ? 'SG 服務金鑰沒過（代理注入的金鑰可能失效，要管理員更新 secret）'
            : res.status === 401
              ? `沒有通過驗證（${detail}）——請重新登入；寫入類動作還需要 API Token`
              : res.status === 403
                ? `被擋下：${detail}`
                : detail;
    const error = new Error(message) as Error & { status?: number; code?: string };
    error.status = res.status;
    if (code !== '') error.code = code;
    throw error;
  }
  return body as T;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const text = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);
const num = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

function listOf(value: unknown, key: string): unknown[] {
  if (Array.isArray(value)) return value;
  const row = asRecord(value);
  return Array.isArray(row[key]) ? (row[key] as unknown[]) : [];
}

export function toGirl(raw: unknown): SgGirl {
  const row = asRecord(raw);
  const id = text(row.id, text(row.girlId, text(row.girl_id)));
  return {
    id,
    nickname: text(row.nickname, text(row.name, id === '' ? '（未命名女孩）' : id)),
    unreadCount: num(row.unreadCount ?? row.unread ?? row.unread_count),
  };
}

export function toConversation(raw: unknown): SgConversation {
  const row = asRecord(raw);
  const id = text(row.id, text(row.conversationId));
  const lastTs = row.lastActivityAt ?? row.lastMessageAt ?? row.updatedAt;
  return {
    id,
    customerName: text(row.customerName, text(row.customer ?? row.name, '（客人）')),
    blocked: row.blocked === true,
    unreadCount: num(row.unreadCount ?? row.unread),
    lastMessagePreview: text(row.lastMessagePreview, text(row.preview, text(row.lastMessage))),
    lastActivityAt: typeof lastTs === 'number' && Number.isFinite(lastTs) ? lastTs : null,
  };
}

export function toSgSession(raw: unknown): SgSession {
  const row = asRecord(raw);
  const girlId = text(row.girlId, text(row.room, text(row.girl_id)));
  return {
    id: text(row.id),
    girlId,
    nickname: text(row.nickname, girlId),
    createdAt: num(row.createdAt),
    mode: parseMode(row.mode ?? row.autoReplyMode, 'draft'),
  };
}

export function toDraft(raw: unknown): SgDraft | null {
  const row = asRecord(raw);
  if (row.type !== 'draft') return null;
  const body = text(row.text, text(row.message));
  if (body === '') return null;
  const ts = typeof row.ts === 'number' && Number.isFinite(row.ts) ? row.ts : Date.now();
  return {
    id: `${ts}:${body.slice(0, 24)}`,
    text: body,
    ts,
    replyTo: typeof row.replyTo === 'string' && row.replyTo !== '' ? row.replyTo : null,
  };
}

/** `GET /api/sg/girls`（經代理）→ 全部女孩（含未讀）。 */
export async function listGirls(): Promise<SgGirl[]> {
  const body = await request<unknown>('/girls', { viaProxy: true });
  return listOf(body, 'girls').map(toGirl).filter((girl) => girl.id !== '');
}

/** `GET /api/sg/girls/:girlId/conversations`（經代理）→ 這位女孩的對話串。 */
export async function listGirlConversations(girlId: string): Promise<SgConversation[]> {
  const body = await request<unknown>(`/girls/${encodeURIComponent(girlId)}/conversations`, {
    viaProxy: true,
  });
  return listOf(body, 'conversations').map(toConversation);
}

/** 已接上的 SG session：`GET /api/sg/sessions`（經代理，機器身分）。 */
export async function listSgSessions(): Promise<SgSession[]> {
  const body = await request<unknown>('/sessions', { viaProxy: true });
  return listOf(body, 'sessions').map(toSgSession).filter((item) => item.id !== '');
}

export type AttachInput = {
  girlId: string;
  /** 指定對話串；省略 = connector 挑最近有訊息的那條。 */
  conversationId: string;
  mode: AutoReplyMode;
};

/**
 * 接上：`POST /api/sessions` 帶 `service:'sg'`（＝後端 `/api/sg/sessions` 轉發的同一條路徑，
 * 但走一般 session 驗證，不需要金鑰）。`autoReplyMode` 預設 `draft`：驗收時絕不自動送出。
 */
export async function attachGirl(input: AttachInput): Promise<SgSession> {
  const body = await request<unknown>('/api/sessions', {
    method: 'POST',
    body: {
      service: 'sg',
      room: input.girlId.trim(),
      nickname: input.girlId.trim(),
      ...(input.conversationId.trim() === '' ? {} : { conversationId: input.conversationId.trim() }),
      autoReplyMode: input.mode,
    },
  });
  return { ...toSgSession(body), mode: parseMode(asRecord(body).autoReplyMode, input.mode) };
}

/** 退出：一般 DELETE（同一份 registry／DO 清理），不需要金鑰。 */
export async function detachSession(sessionId: string): Promise<void> {
  await request(`/api/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
}

export async function getAutoReply(sessionId: string): Promise<AutoReplyStatus> {
  const row = asRecord(
    await request<unknown>(`/api/sessions/${encodeURIComponent(sessionId)}/auto-reply`),
  );
  return {
    mode: parseMode(row.mode, 'draft'),
    hasPersona: row.hasPersona === true,
    personaName: typeof row.personaName === 'string' ? row.personaName : null,
    personaSource: text(row.personaSource, '—'),
    repliesLastHour: num(row.repliesLastHour),
  };
}

/** 切模式：`{mode}` 只帶這一個欄位，persona 不會被清掉（後端語意）。 */
export async function setAutoReply(sessionId: string, mode: AutoReplyMode): Promise<AutoReplyMode> {
  const row = asRecord(
    await request<unknown>(`/api/sessions/${encodeURIComponent(sessionId)}/auto-reply`, {
      method: 'POST',
      body: { mode },
    }),
  );
  return parseMode(row.mode, mode);
}

/** 最近草稿：`GET /api/sessions/:id/events` 裡 `type==='draft'` 的那幾筆（新到舊）。 */
export async function listDrafts(sessionId: string, limit = 40): Promise<SgDraft[]> {
  const body = await request<unknown>(
    `/api/sessions/${encodeURIComponent(sessionId)}/events?limit=${limit}`,
  );
  return listOf(body, 'events')
    .map(toDraft)
    .filter((item): item is SgDraft => item !== null)
    .sort((a, b) => b.ts - a.ts);
}
